import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import {
  fetchCategories,
  selectCategories,
  selectCategoriesCount,
  selectCategoriesError,
  selectCategoriesStatus,
} from "../features/categories/categoriesSlice";

export default function CategoriesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const PAGE_SIZE = 25;

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const rows = useSelector(selectCategories);
  const totalCount = useSelector(selectCategoriesCount);
  const loadStatus = useSelector(selectCategoriesStatus);
  const error = useSelector(selectCategoriesError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);

  // Keep URL in sync, so Back/Reload preserves pagination/filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [page, searchTrimmed, setSearchParams]);

  // Hydrate local state from URL (Back/Forward)
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlPageRaw = searchParams.get("page");
    const urlPageNum = Number(urlPageRaw || 1);
    const urlPage = Number.isFinite(urlPageNum) && urlPageNum > 0 ? Math.floor(urlPageNum) : 1;

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchCategories({ search: searchTrimmed, page, pageSize: PAGE_SIZE }));
  }, [dispatch, refreshKey, searchTrimmed, page]);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  useEffect(() => {
    // Clamp only after data is loaded to avoid resetting page on hard reload.
    if (loadStatus === "succeeded" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  const visibleRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Категории</div>
          <div style={styles.subTitle}>
            Список категорий (данные из <span style={styles.mono}>/api/product/categories/</span>).
          </div>
        </div>

        <div style={styles.headerActions}>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: name / slug / description…"
            style={styles.search}
          />
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} style={styles.btn}>
            Обновить
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/warehouse/categories/new", {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            style={styles.btnPrimary}
          >
            + Добавить категорию
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Slug</th>
                <th style={styles.th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={4} style={styles.tdMuted}>
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    style={styles.tr}
                    onClick={() =>
                      navigate(`/warehouse/categories/${r.id}/edit`, {
                        state: { from: `${location.pathname}${location.search}` },
                      })
                    }
                    title="Открыть категорию"
                  >
                    <td style={styles.tdMono}>{r.id}</td>
                    <td style={styles.td}>{r.name || "—"}</td>
                    <td style={styles.tdMono}>{r.slug || "—"}</td>
                    <td style={styles.td}>{r.description || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
            styles={styles}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
  },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 20, fontWeight: 900 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  search: {
    width: 360,
    maxWidth: "70vw",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  notice: { padding: "12px 18px", fontSize: 15, opacity: 0.85 },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
  },
  body: { padding: 18, minHeight: 0, overflow: "auto" },
  tableWrap: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  tr: { cursor: "pointer" },
  td: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  tdMuted: {
    padding: 14,
    fontSize: 15,
    opacity: 0.75,
  },
  paginationWrap: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  paginationMeta: { fontSize: 14, opacity: 0.8, fontWeight: 900 },
  paginationBtns: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  pageBtn: {
    padding: "8px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 14,
    minWidth: 36,
  },
  pageBtnActive: {
    border: "1px solid rgba(245,197,24,0.4)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
  },
  pageBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  pageDots: { opacity: 0.7, fontWeight: 900, padding: "0 6px" },
};

